import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BanIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CodeIcon,
  Loader2Icon,
  MinusIcon,
  PencilIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from '../components/icons.js';
import { Icon } from '../components/Icon.js';
import {
  CODE_SOURCE_DEFAULT_EXCLUDE_GLOBS,
  CODE_SOURCE_DEFAULT_INCLUDE_GLOBS,
  type CodeProvider,
  type CodeSource,
  type CodeSyncFileEntry,
  type CodeSyncProgress,
} from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';

/**
 * OneNebula Code Sources admin page. Phase 6 of the OneView design rollout —
 * dense OneView table (NOT a card grid; Code Sources has many more rows than
 * Confluence and density matters).
 *
 * Operators register a Bitbucket / Azure DevOps / GitHub repo by `repoRef`
 * (gateway-defined opaque slug). The unified gateway at aiagents.1nebula.com
 * resolves the underlying provider; we never see provider-specific URLs.
 *
 * Sync calls return 202 + jobId and progress is polled per-row via
 * GET /api/v1/admin/code-sources/:id every 2.5s while status === 'running'.
 */

interface SyncResponse {
  status: 'queued' | 'succeeded' | 'failed';
  pagesIngested: number;
  lastCommitSha?: string;
  jobId?: string;
  fileCount?: number;
  errorCode?: string;
  errorMessage?: string;
}

type ProviderFilter = 'all' | 'github' | 'bitbucket' | 'azure_devops';

const PROVIDER_LABEL: Record<CodeProvider, string> = {
  github: 'GitHub',
  bitbucket: 'Bitbucket',
  atlassian_rovo: 'Atlassian',
  azure_devops: 'Azure DevOps',
};

const PROVIDER_SHORT: Record<CodeProvider, string> = {
  github: 'GH',
  bitbucket: 'BB',
  atlassian_rovo: 'AT',
  azure_devops: 'ADO',
};

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

function formatDate(iso?: string): string {
  if (!iso) return 'Never synced';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/**
 * OneView status chip mapping (Phase 5 parity). `CodeSourceStatus` only has
 * idle | running | succeeded | failed — no `partial` variant in this schema,
 * so any future addition falls through to the neutral `idle` style.
 */
function statusBadge(status: CodeSource['status']): JSX.Element {
  const styles: Record<string, string> = {
    idle: 'bg-nebula-gray-5 text-nebula-fg-3',
    running: 'bg-nebula-tint text-nebula-secondary',
    succeeded: 'bg-nebula-success-bg text-nebula-success',
    partial: 'bg-nebula-warning-bg text-nebula-warning',
    failed: 'bg-nebula-error-bg text-nebula-error',
  };
  const labels: Record<string, string> = {
    idle: 'Idle',
    running: 'Syncing',
    succeeded: 'Succeeded',
    partial: 'Partial',
    failed: 'Failed',
  };
  const style = styles[status] ?? styles.idle;
  const label = labels[status] ?? String(status);
  return (
    <span
      className={`ov-meta-upper inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 ${style}`}
    >
      {status === 'running' && (
        <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-nebula-secondary" />
      )}
      {label}
    </span>
  );
}

/**
 * Status cell — badge + a tiny outcome icon. For `failed` the icon
 * opens a hover popover with the full error message instead of
 * dumping it inline (long upstream gateway errors used to overflow
 * the table and trigger horizontal scroll that wasn't obvious to the
 * user). For `succeeded` we render a green tick as a symmetric
 * affordance. Other statuses get nothing extra — the badge alone is
 * enough.
 */
function StatusCell({
  status,
  errorMessage,
}: {
  status: CodeSource['status'];
  errorMessage?: string;
}): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1.5">
      {statusBadge(status)}
      {status === 'succeeded' && (
        <span
          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-nebula-success-bg text-nebula-success"
          aria-label="Sync succeeded"
          title="Sync succeeded"
        >
          <CheckIcon size={12} />
        </span>
      )}
      {status === 'failed' && errorMessage && <ErrorHoverIcon errorMessage={errorMessage} />}
    </div>
  );
}

/**
 * Small error-icon with a hover-revealed popover. Pure CSS via
 * `group-hover` so we don't need a portal or click-outside handler.
 * The popover anchors to the right edge of the icon and is positioned
 * left-of-cursor with a fixed max-width so long Cloud Storage / ADO
 * error URLs wrap inside it instead of stretching the row.
 */
function ErrorHoverIcon({ errorMessage }: { errorMessage: string }): JSX.Element {
  // Tooltip is rendered into a portal with fixed positioning so the
  // table wrapper's `overflow-x-auto overflow-y-clip` (required for the
  // horizontally-scrolling table) doesn't crop popovers off the bottom
  // row. Pick above-vs-below based on viewport room: if there's less
  // than the tooltip height under the icon, open upward instead.
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{
    top: number;
    right: number;
    placement: 'above' | 'below';
  } | null>(null);
  const TOOLTIP_HEIGHT_GUESS = 160;
  const GAP = 6;
  const show = useCallback((): void => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const spaceBelow = window.innerHeight - r.bottom;
    const placement = spaceBelow < TOOLTIP_HEIGHT_GUESS && r.top > spaceBelow ? 'above' : 'below';
    const top = placement === 'below' ? r.bottom + GAP : r.top - GAP;
    setPos({ top, right: window.innerWidth - r.right, placement });
  }, []);
  const hide = useCallback((): void => setPos(null), []);
  return (
    <span className="inline-flex">
      <button
        ref={btnRef}
        type="button"
        aria-label={`Sync error: ${errorMessage}`}
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-nebula-error-bg text-nebula-error hover:bg-nebula-error hover:text-white focus:outline-none focus:ring-2 focus:ring-nebula-error"
      >
        <Icon name="priority_high" size={13} />
      </button>
      {pos &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-50 w-80 rounded-md border border-nebula-error/30 bg-nebula-surface p-2.5 text-left text-[11px] leading-snug text-nebula-fg-1 shadow-lg"
            style={{
              top: pos.placement === 'below' ? pos.top : undefined,
              bottom: pos.placement === 'above' ? window.innerHeight - pos.top : undefined,
              right: pos.right,
              boxShadow: 'var(--ov-shadow-popover, 0 10px 24px rgba(14,12,20,0.18))',
            }}
          >
            <span className="mb-1 block font-ui text-[10px] font-semibold uppercase tracking-wider text-nebula-error">
              Last sync error
            </span>
            <span className="block whitespace-pre-wrap break-words font-mono text-nebula-fg-2">
              {errorMessage}
            </span>
          </div>,
          document.body,
        )}
    </span>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

function relativeTime(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now - then);
  if (diff < 1000) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function decodeFilename(filename: string): string {
  // Path separator was escaped to `__` at registry-write time.
  return filename.replace(/__/g, '/');
}

function globsToText(globs: string[] | undefined, fallback: readonly string[]): string {
  if (!globs || globs.length === 0) return fallback.join('\n');
  return globs.join('\n');
}

function textToGlobs(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// ---------- Live progress UI ------------------------------------------------

const FILE_STATUS_LABEL: Record<CodeSyncFileEntry['status'], string> = {
  in_progress: 'In progress',
  succeeded: 'Succeeded',
  failed: 'Failed',
  skipped: 'Skipped',
};

function FileStatusIcon({ status }: { status: CodeSyncFileEntry['status'] }): JSX.Element {
  if (status === 'in_progress')
    return <Loader2Icon size={12} className="animate-spin text-nebula-secondary" />;
  if (status === 'succeeded') return <CheckIcon size={12} className="text-nebula-success" />;
  if (status === 'skipped') return <MinusIcon size={12} className="text-nebula-fg-4" />;
  if (status === 'failed') return <XIcon size={12} className="text-nebula-error" />;
  return <BanIcon size={12} className="text-nebula-fg-4" />;
}

function CodeSyncProgressStrip({
  progress,
  now,
}: {
  progress: NonNullable<CodeSource['syncProgress']>;
  now: number;
}): JSX.Element {
  const total = Math.max(progress.totalFiles, 1);
  const pct = Math.min(100, Math.round((progress.currentIndex / total) * 100));
  const phaseLabel: Record<typeof progress.phase, string> = {
    listing: 'Listing',
    fetching: 'Fetching',
    ingesting: 'Ingesting',
    completed: 'Completed',
  };
  const isListing = progress.phase === 'listing';
  const isTruncated = progress.treeTruncated === true;
  const elapsedMs = Math.max(0, now - Date.parse(progress.startedAt));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs text-nebula-fg-2">
        {isListing ? (
          <span className="font-semibold text-nebula-fg-1">Walking the repository tree…</span>
        ) : (
          <>
            <span className="font-semibold text-nebula-fg-1">
              Syncing file {progress.currentIndex}/{progress.totalFiles}
            </span>
            {progress.currentPath && (
              <span className="truncate text-nebula-fg-3">
                — {truncate(progress.currentPath, 60)}
              </span>
            )}
          </>
        )}
        <span className="ov-meta-upper ml-auto inline-flex items-center gap-1">
          <span className="rounded-md bg-nebula-tint px-1.5 py-0.5 text-nebula-secondary">
            {phaseLabel[progress.phase]}
          </span>
          <span className="text-nebula-fg-3" title={`Started ${progress.startedAt}`}>
            · {formatElapsed(elapsedMs)}
          </span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-nebula-surface">
        {isListing ? (
          // Indeterminate bar — listing phase has no totalFiles yet.
          <div className="h-full w-1/3 animate-pulse bg-nebula-secondary" />
        ) : (
          <div
            className="h-full bg-nebula-secondary transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      {isListing ? (
        <div className="ov-meta text-nebula-fg-3">
          BFS traversal at ~1 dir/sec (gateway throttle). Mega-repos can take 5–15 min before the
          file list is built.
        </div>
      ) : (
        <div className="ov-meta text-nebula-fg-3">
          {progress.filesSucceeded} succeeded · {progress.filesFailed} failed ·{' '}
          {progress.filesSkipped} skipped
        </div>
      )}
      {isTruncated && (
        <div className="ov-meta inline-flex items-center gap-1 rounded-md bg-nebula-warning-bg px-2 py-1 text-nebula-warning">
          <span aria-hidden>⚠</span>
          Tree truncated — listTree hit its call/entry cap. Indexed view of this repo is partial.
          Tighten <code>excludeGlobs</code> or raise <code>MAX_CALLS</code>.
        </div>
      )}
    </div>
  );
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return rs > 0 ? `${m}m ${rs}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function LiveFileLog({
  entries,
  defaultCollapsed,
}: {
  entries: NonNullable<CodeSyncProgress['recentFiles']>;
  defaultCollapsed: boolean;
}): JSX.Element | null {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (collapsed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [collapsed]);
  if (!entries || entries.length === 0) return null;
  const ordered = [...entries].reverse();
  return (
    <div className="mb-3 rounded-md border border-nebula-border-soft bg-nebula-gray-5 px-3 py-2">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="ov-meta-upper flex w-full items-center gap-1.5 text-nebula-fg-3 hover:text-nebula-fg-1"
      >
        {collapsed ? <ChevronRightIcon size={12} /> : <ChevronDownIcon size={12} />}
        Live progress (last {entries.length} {entries.length === 1 ? 'file' : 'files'})
      </button>
      {!collapsed && (
        <ul className="mt-1.5 space-y-0.5">
          {ordered.map((e) => (
            <li key={`${e.path}-${e.ts}`} className="text-[12px] leading-snug">
              <div className="flex items-center gap-2">
                <span className="flex-shrink-0" aria-label={FILE_STATUS_LABEL[e.status]}>
                  <FileStatusIcon status={e.status} />
                </span>
                <span
                  className="flex-1 truncate font-mono text-[11px] text-nebula-fg-1"
                  title={e.path}
                >
                  {truncate(e.path, 80)}
                </span>
                <span className="ov-numeric flex-shrink-0 text-[11px] text-nebula-fg-4">
                  {relativeTime(e.ts, now)}
                  {typeof e.durationMs === 'number' && e.status !== 'in_progress'
                    ? ` · +${e.durationMs}ms`
                    : ''}
                  {typeof e.chunkCount === 'number' && e.status === 'succeeded'
                    ? ` · ${e.chunkCount} chunks`
                    : ''}
                </span>
              </div>
              {e.errorMessage && (
                <div
                  className="ml-5 truncate font-mono text-[11px] text-nebula-error"
                  title={e.errorMessage}
                >
                  {truncate(e.errorMessage, 200)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Per-row poll. Fires GET /code-sources/:id every 2.5s while `enabled`.
 * Localised here so only the running row re-renders. Matches the
 * useSourcePoll hook in AdminConfluence.tsx.
 */
function useSourcePoll(id: string, enabled: boolean, onUpdate: (source: CodeSource) => void): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const tick = (): void => {
      apiGet<{ source: CodeSource }>(`/api/v1/admin/code-sources/${id}`)
        .then((res) => {
          if (!cancelled) onUpdate(res.source);
        })
        .catch(() => {
          /* transient — next tick retries. */
        });
    };
    const t = setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [id, enabled, onUpdate]);
}

// ---------- per-source file list panel --------------------------------------

interface CodeFileRow {
  filename: string;
  documentTitle?: string;
  chunkCount?: number;
  lastIngestedAt?: string;
  codeLanguage?: string;
  ring?: 'ring0' | 'ring1';
}

interface FilesResponse {
  source: CodeSource;
  files: CodeFileRow[];
  totalChunks: number;
}

function SourceFilesPanel({
  source,
  onError,
}: {
  source: CodeSource;
  onError: (msg: string) => void;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<CodeFileRow[] | null>(null);
  const [totalChunks, setTotalChunks] = useState(0);
  const [reindexing, setReindexing] = useState<Record<string, boolean>>({});
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<FilesResponse>(`/api/v1/admin/code-sources/${source.id}/files`)
      .then((res) => {
        if (cancelled) return;
        setFiles(res.files);
        setTotalChunks(res.totalChunks);
      })
      .catch((err: unknown) => {
        if (!cancelled) onError(errorMessage(err, 'Could not load files.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source.id, reload, onError]);

  const onReindex = useCallback(
    async (filename: string) => {
      setReindexing((s) => ({ ...s, [filename]: true }));
      try {
        await apiPost(
          `/api/v1/admin/code-sources/${source.id}/files/${encodeURIComponent(filename)}/resync`,
          {},
        );
        setReload((n) => n + 1);
      } catch (err) {
        onError(errorMessage(err, 'Re-sync failed.'));
      } finally {
        setReindexing((s) => ({ ...s, [filename]: false }));
      }
    },
    [source.id, onError],
  );

  if (loading && files === null) {
    return <div className="ov-body py-3 text-nebula-fg-3">Loading files…</div>;
  }
  if (files !== null && files.length === 0) {
    return (
      <div className="ov-body py-3 text-nebula-fg-3">
        No files from this source yet. Run <strong>Sync delta</strong> to ingest the repo.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-nebula-border-soft">
      <table className="w-full text-sm">
        <thead className="border-b border-nebula-border bg-nebula-gray-6">
          <tr>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">File</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Chunks</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Language</th>
            <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Last indexed</th>
            <th className="ov-meta-upper px-3 py-2 text-right text-nebula-fg-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {(files ?? []).map((f) => {
            const displayPath = decodeFilename(f.filename);
            return (
              <tr
                key={f.filename}
                className="border-b border-nebula-border-soft hover:bg-nebula-tint"
              >
                <td
                  className="px-3 py-2 font-mono text-[12px] text-nebula-fg-1"
                  title={displayPath}
                >
                  {truncate(displayPath, 80)}
                </td>
                <td className="ov-numeric px-3 py-2 text-nebula-fg-2">{f.chunkCount ?? 0}</td>
                <td className="px-3 py-2 text-[12px] text-nebula-fg-3">{f.codeLanguage ?? '—'}</td>
                <td className="px-3 py-2 text-[12px] text-nebula-fg-3">
                  {formatDate(f.lastIngestedAt)}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onReindex(f.filename)}
                    disabled={reindexing[f.filename]}
                    className="inline-flex items-center gap-1.5 rounded-md border border-nebula-border bg-nebula-surface px-2 py-1 text-[11px] font-ui text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-60"
                  >
                    <RefreshCwIcon
                      size={11}
                      className={reindexing[f.filename] ? 'animate-spin' : ''}
                    />
                    Re-sync
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td
              colSpan={5}
              className="border-t border-nebula-border-soft bg-nebula-gray-5 px-3 py-2 text-right text-[11px] text-nebula-fg-3"
            >
              Total files: {files?.length ?? 0} · Total chunks: {totalChunks}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---------- Provider chip ---------------------------------------------------

function ProviderChip({ provider }: { provider: CodeProvider }): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md bg-nebula-gray-5 px-2 py-0.5 text-nebula-fg-2"
      title={PROVIDER_LABEL[provider]}
    >
      <CodeIcon size={12} className="text-nebula-fg-3" />
      <span className="font-ui text-[11px] font-semibold">{PROVIDER_SHORT[provider]}</span>
    </span>
  );
}

// ---------- SourceRow -------------------------------------------------------

interface SourceRowProps {
  source: CodeSource;
  expanded: boolean;
  syncing: boolean;
  onToggleExpand: (id: string) => void;
  onSync: (id: string, force?: boolean) => void;
  onEdit: (s: CodeSource) => void;
  onRequestDelete: (s: CodeSource) => void;
  onSourceUpdate: (s: CodeSource) => void;
  onError: (msg: string) => void;
  onToast: (msg: string) => void;
}

const SourceRow = memo(function SourceRow({
  source: s,
  expanded,
  syncing,
  onToggleExpand,
  onSync,
  onEdit,
  onRequestDelete,
  onSourceUpdate,
  onError,
}: SourceRowProps): JSX.Element {
  useSourcePoll(s.id, s.status === 'running', onSourceUpdate);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState<'down' | 'up'>('down');
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (ev: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(ev.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Viewport-aware flip: if the menu would overflow the bottom of the
  // viewport (last row in the table, etc.), open it upward instead.
  const toggleMenu = (): void => {
    setMenuOpen((open) => {
      if (open) return false;
      const btn = triggerRef.current;
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const MENU_ESTIMATED_HEIGHT = 200; // 4-5 items × ~36px + padding
        const spaceBelow = window.innerHeight - rect.bottom;
        setMenuPlacement(spaceBelow < MENU_ESTIMATED_HEIGHT ? 'up' : 'down');
      }
      return true;
    });
  };

  const recentFiles = s.syncProgress?.recentFiles ?? [];
  const terminal = s.status === 'succeeded' || s.status === 'failed';
  // Tick once a second while running so the elapsed-time chip updates between
  // the parent's 2.5s polls — gives a "this is alive" signal during the long
  // listing phase where no other UI state changes.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (s.status !== 'running') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [s.status]);
  const lastSyncRel = s.lastSyncedAt ? relativeTime(s.lastSyncedAt, now) : null;
  // Mini recent-files list shown in the inline expanded sub-row while running.
  const miniRecent =
    s.status === 'running' && recentFiles.length > 0 ? [...recentFiles].reverse().slice(0, 3) : [];

  return (
    <Fragment>
      <tr className="border-b border-nebula-border-soft hover:bg-nebula-tint">
        <td className="px-3 py-2">
          <ProviderChip provider={s.provider} />
        </td>
        <td className="px-3 py-2">
          <button
            type="button"
            onClick={() => onToggleExpand(s.id)}
            className="block max-w-md truncate text-left font-mono text-sm text-nebula-fg-1 hover:underline"
            title={s.repoRef}
          >
            {s.label ? `${s.label} ` : ''}
            <span className={s.label ? 'text-nebula-fg-3' : ''}>{s.repoRef}</span>
          </button>
        </td>
        <td className="px-3 py-2">
          <span className="inline-flex items-center rounded-full bg-nebula-gray-5 px-2 py-0.5 font-mono text-xs text-nebula-fg-2">
            {s.branch}
          </span>
        </td>
        <td className="ov-numeric px-3 py-2 text-nebula-fg-2">{s.pagesIngested}</td>
        <td className="px-3 py-2">
          <StatusCell status={s.status} errorMessage={s.errorMessage} />
        </td>
        <td className="ov-meta px-3 py-2 text-nebula-fg-3">
          {lastSyncRel ?? 'Never synced'}
          {s.lastCommitSha && (
            <div className="font-mono text-[11px] text-nebula-fg-4">
              {s.lastCommitSha.slice(0, 7)}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right">
          <div className="relative inline-block" ref={menuRef}>
            <button
              ref={triggerRef}
              type="button"
              onClick={toggleMenu}
              aria-label="Row actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              disabled={syncing}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-nebula-border bg-nebula-surface text-nebula-fg-2 hover:bg-nebula-tint disabled:opacity-60"
            >
              <Icon name="more_vert" size={16} />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className={`absolute right-0 z-20 w-44 overflow-hidden rounded-md border border-nebula-border-soft bg-nebula-surface shadow-lg ${
                  menuPlacement === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
                }`}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onSync(s.id, false);
                  }}
                  disabled={s.status === 'running'}
                  className="ov-body flex w-full items-center gap-2 px-3 py-2 text-left text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-50"
                >
                  <RefreshCwIcon size={14} />
                  Sync delta
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onSync(s.id, true);
                  }}
                  disabled={s.status === 'running'}
                  className="ov-body flex w-full items-center gap-2 px-3 py-2 text-left text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-50"
                >
                  <RefreshCwIcon size={14} />
                  Force re-ingest
                </button>
                <div className="border-t border-nebula-border-soft" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(s);
                  }}
                  className="ov-body flex w-full items-center gap-2 px-3 py-2 text-left text-nebula-fg-1 hover:bg-nebula-tint"
                >
                  <PencilIcon size={14} />
                  Edit
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onRequestDelete(s);
                  }}
                  className="ov-body flex w-full items-center gap-2 px-3 py-2 text-left text-nebula-error hover:bg-nebula-error-bg"
                >
                  <Trash2Icon size={14} />
                  Delete
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
      {s.status === 'running' && s.syncProgress && (
        <tr className="border-b border-nebula-border-soft">
          {/*
            Sub-row spans all 7 columns; the table itself is `min-w-[960px]`
            inside an `overflow-x-auto` wrapper, so when the table scrolls
            horizontally the sub-row content would scroll with it and clip
            at both edges. Pin the inner div with `sticky left-0` so the
            live status stays readable regardless of the horizontal scroll
            position, then constrain its width to the viewport-side of the
            scroll container with `max-w-[calc(100vw-20rem)]` (rough offset
            for the sidebar). The `p-0` on the td hands all padding to the
            inner div so the sticky element controls its own bounds.
          */}
          <td colSpan={7} className="bg-nebula-tint p-0">
            <div className="sticky left-0 max-w-[calc(100vw-20rem)] px-4 py-3">
              <CodeSyncProgressStrip progress={s.syncProgress} now={now} />
              {miniRecent.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-nebula-border-soft pt-2">
                  {miniRecent.map((e) => (
                    <li
                      key={`${e.path}-${e.ts}`}
                      className="flex items-center gap-2 text-[12px] leading-snug"
                    >
                      <span className="flex-shrink-0" aria-label={FILE_STATUS_LABEL[e.status]}>
                        <FileStatusIcon status={e.status} />
                      </span>
                      <span
                        className="flex-1 truncate font-mono text-[11px] text-nebula-fg-1"
                        title={e.path}
                      >
                        {truncate(e.path, 70)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
      {expanded && (
        <tr className="border-b border-nebula-border-soft">
          <td colSpan={7} className="bg-nebula-surface px-4 pb-4 pt-3">
            {recentFiles.length > 0 && (
              <LiveFileLog entries={recentFiles} defaultCollapsed={terminal} />
            )}
            <SourceFilesPanel source={s} onError={onError} />
          </td>
        </tr>
      )}
    </Fragment>
  );
});

// ---------- page -----------------------------------------------------------

export function AdminCodeSourcesPage(): JSX.Element {
  const [sources, setSources] = useState<CodeSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [topToast, setTopToast] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<CodeSource | null>(null);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [pendingDelete, setPendingDelete] = useState<CodeSource | null>(null);
  const [reload, setReload] = useState(0);
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const [providerFilter, setProviderFilter] = useState<ProviderFilter>('all');
  const [search, setSearch] = useState('');

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const toggleExpanded = useCallback(
    (id: string) => setExpandedSources((s) => ({ ...s, [id]: !s[id] })),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTopError(null);
    apiGet<{ sources: CodeSource[] }>('/api/v1/admin/code-sources')
      .then((res) => {
        if (!cancelled) setSources(res.sources);
      })
      .catch((err: unknown) => {
        if (!cancelled) setTopError(errorMessage(err, 'Could not load code sources.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const replaceSource = useCallback((next: CodeSource) => {
    setSources((prev) => {
      const idx = prev.findIndex((s) => s.id === next.id);
      if (idx < 0) return prev;
      if (prev[idx] === next) return prev;
      const copy = prev.slice();
      copy[idx] = next;
      return copy;
    });
  }, []);

  const onSync = useCallback(
    async (id: string, force = false) => {
      // Default = delta sync: backend short-circuits when the head
      // commit SHA matches the last-synced one. Force = true bypasses
      // that check and triggers a full re-walk + re-embed. Wired into
      // backend route /:id/sync at admin/code-sources.ts.
      setSyncing((s) => ({ ...s, [id]: true }));
      try {
        const resp = await apiPost<SyncResponse>(`/api/v1/admin/code-sources/${id}/sync`, {
          force,
        });
        if (resp && (resp as { status?: string }).status === 'unchanged') {
          setTopToast('Already up to date — no new commits to ingest.');
        } else if (force) {
          setTopToast('Force re-ingest queued — track progress on the row.');
        } else {
          setTopToast('Sync queued — track progress on the row.');
        }
        apiGet<{ source: CodeSource }>(`/api/v1/admin/code-sources/${id}`)
          .then((res) => replaceSource(res.source))
          .catch(() => undefined);
      } catch (err) {
        setTopError(errorMessage(err, 'Sync failed.'));
      } finally {
        setSyncing((s) => ({ ...s, [id]: false }));
      }
    },
    [replaceSource],
  );

  const onDelete = useCallback(
    async (source: CodeSource) => {
      try {
        await apiDelete(`/api/v1/admin/code-sources/${source.id}`);
        setPendingDelete(null);
        refresh();
      } catch (err) {
        setTopError(errorMessage(err, 'Delete failed.'));
      }
    },
    [refresh],
  );

  // KPI derivations — all from existing state, no new API calls.
  const kpis = useMemo(() => {
    const totalRepos = sources.length;
    let totalChunks = 0;
    let activeSyncs = 0;
    const providerCounts: Record<string, number> = {};
    for (const s of sources) {
      totalChunks += s.pagesIngested ?? 0;
      if (s.status === 'running') activeSyncs += 1;
      providerCounts[s.provider] = (providerCounts[s.provider] ?? 0) + 1;
    }
    return { totalRepos, totalChunks, activeSyncs, providerCounts };
  }, [sources]);

  const providerSummary = useMemo(() => {
    const c = kpis.providerCounts;
    const parts: string[] = [];
    if (c.github) parts.push(`GH: ${c.github}`);
    if (c.bitbucket) parts.push(`BB: ${c.bitbucket}`);
    if (c.azure_devops) parts.push(`ADO: ${c.azure_devops}`);
    if (c.atlassian_rovo) parts.push(`AT: ${c.atlassian_rovo}`);
    return parts.join(' · ') || 'None yet';
  }, [kpis.providerCounts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sources.filter((s) => {
      if (providerFilter !== 'all' && s.provider !== providerFilter) return false;
      if (q && !s.repoRef.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sources, providerFilter, search]);

  const filterChips: { value: ProviderFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'github', label: 'GitHub' },
    { value: 'bitbucket', label: 'Bitbucket' },
    { value: 'azure_devops', label: 'Azure DevOps' },
  ];

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="ov-h-page">Code Sources</h1>
            <p className="ov-body mt-1 text-nebula-fg-3">
              Sync GitHub, Bitbucket, and Azure DevOps repos via the AI Agent Catalogue gateway.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90"
          >
            Connect new repo
          </button>
        </header>

        {/*
          KPI tiles — show em-dash placeholders while the sources list is
          still loading. The previous version flashed "0 / No repos yet"
          for ~3s during the first paint because `sources` is `[]` until
          the API resolves.
        */}
        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiTile
            label="Total repos"
            value={loading && sources.length === 0 ? '—' : kpis.totalRepos.toLocaleString('en-GB')}
            sub={
              loading && sources.length === 0
                ? undefined
                : kpis.totalRepos === 0
                  ? 'No repos yet'
                  : undefined
            }
          />
          <KpiTile
            label="Total chunks"
            value={
              loading && sources.length === 0
                ? '—'
                : kpis.totalChunks > 0
                  ? kpis.totalChunks.toLocaleString('en-GB')
                  : '—'
            }
            sub={
              !loading && kpis.totalRepos > 0
                ? `${Math.round(kpis.totalChunks / Math.max(1, kpis.totalRepos)).toLocaleString('en-GB')} avg / repo`
                : undefined
            }
          />
          <KpiTile
            label="Active syncs"
            value={loading && sources.length === 0 ? '—' : kpis.activeSyncs.toLocaleString('en-GB')}
            sub={
              loading && sources.length === 0
                ? undefined
                : kpis.activeSyncs > 0
                  ? 'Syncing now'
                  : 'Idle'
            }
          />
          <KpiTile
            label="Providers"
            value={loading && sources.length === 0 ? '—' : providerSummary}
          />
        </div>

        {topError && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-nebula-error/20 bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error">
            <span>{topError}</span>
            <button
              type="button"
              onClick={() => setTopError(null)}
              aria-label="Close error message"
              className="text-nebula-error/60 hover:text-nebula-error"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}
        {topToast && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-nebula-secondary/20 bg-nebula-tint px-3 py-2 text-sm text-nebula-secondary">
            <span>{topToast}</span>
            <button
              type="button"
              onClick={() => setTopToast(null)}
              aria-label="Close toast"
              className="text-nebula-secondary/60 hover:text-nebula-secondary"
            >
              <XIcon size={14} />
            </button>
          </div>
        )}

        {/* Filter chip row + search */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {filterChips.map((chip) => {
              const active = providerFilter === chip.value;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => setProviderFilter(chip.value)}
                  className={`rounded-md px-3 py-1.5 font-ui text-sm transition-colors ${
                    active
                      ? 'bg-nebula-primary text-white'
                      : 'border border-nebula-border bg-nebula-surface text-nebula-fg-2 hover:bg-nebula-tint'
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <SearchIcon
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-nebula-fg-4"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by repo reference…"
              className="w-64 rounded-md border border-nebula-border bg-nebula-surface py-1.5 pl-8 pr-3 text-sm text-nebula-fg-1 placeholder:text-nebula-fg-4 focus:border-nebula-secondary focus:outline-none"
            />
          </div>
        </div>

        {loading ? (
          <div className="ov-body text-nebula-fg-3">Loading…</div>
        ) : sources.length === 0 ? (
          <div className="rounded-md border border-dashed border-nebula-border bg-nebula-surface p-10 text-center">
            <CodeIcon size={28} className="mx-auto text-nebula-fg-4" />
            <p className="ov-body mt-3 text-nebula-fg-3">
              No code sources configured yet. Add a GitHub, Bitbucket, or Azure DevOps repo to make
              its source code searchable from the Knowledge Agent.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-dashed border-nebula-border bg-nebula-surface p-10 text-center">
            <p className="ov-body text-nebula-fg-3">
              No repos match the current filter. Clear the filter or search to see all sources.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-clip rounded-md border border-nebula-border-soft bg-nebula-surface">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-nebula-border bg-nebula-gray-6">
                <tr>
                  <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Provider</th>
                  <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Repository</th>
                  <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Branch</th>
                  <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Files</th>
                  <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Status</th>
                  <th className="ov-meta-upper px-3 py-2 text-left text-nebula-fg-2">Last sync</th>
                  <th className="ov-meta-upper px-3 py-2 text-right text-nebula-fg-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <SourceRow
                    key={s.id}
                    source={s}
                    expanded={!!expandedSources[s.id]}
                    syncing={!!syncing[s.id]}
                    onToggleExpand={toggleExpanded}
                    onSync={onSync}
                    onEdit={setEditing}
                    onRequestDelete={setPendingDelete}
                    onSourceUpdate={replaceSource}
                    onError={setTopError}
                    onToast={setTopToast}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <CodeSourceFormModal
          mode="create"
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            refresh();
          }}
          onError={(msg) => setTopError(msg)}
        />
      )}
      {editing && (
        <CodeSourceFormModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
          onError={(msg) => setTopError(msg)}
        />
      )}
      {pendingDelete && (
        <DeleteCodeSourceModal
          source={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => onDelete(pendingDelete)}
        />
      )}
    </Layout>
  );
}

// ---------- KPI tile --------------------------------------------------------

function KpiTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-nebula-border-soft bg-nebula-surface p-4 transition-shadow hover:shadow-sm">
      <div className="ov-meta-upper text-nebula-fg-3">{label}</div>
      <div className="ov-numeric mt-2 text-2xl text-nebula-fg-1">{value}</div>
      {sub && <div className="ov-meta mt-1 text-nebula-fg-3">{sub}</div>}
    </div>
  );
}

// ---------- form modal (create + edit) -------------------------------------

interface FormModalProps {
  mode: 'create' | 'edit';
  initial?: CodeSource;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function CodeSourceFormModal({
  mode,
  initial,
  onClose,
  onSaved,
  onError,
}: FormModalProps): JSX.Element {
  const [provider, setProvider] = useState<CodeProvider>(initial?.provider ?? 'github');
  const [repoRef, setRepoRef] = useState(initial?.repoRef ?? '');
  const [branch, setBranch] = useState(initial?.branch ?? 'main');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [ring, setRing] = useState<'ring0' | 'ring1'>(initial?.ring ?? 'ring1');
  const [defaultClassification, setDefaultClassification] = useState<
    'public' | 'internal' | 'confidential' | 'restricted'
  >(
    (initial as { defaultClassification?: 'public' | 'internal' | 'confidential' | 'restricted' })
      ?.defaultClassification ?? 'internal',
  );
  const [defaultAccessGroups, setDefaultAccessGroups] = useState<string[]>(
    (initial as { defaultAccessGroups?: string[] })?.defaultAccessGroups ?? [],
  );
  const [applyToExisting, setApplyToExisting] = useState(true);
  const [includeText, setIncludeText] = useState(
    globsToText(initial?.includeGlobs, CODE_SOURCE_DEFAULT_INCLUDE_GLOBS),
  );
  const [excludeText, setExcludeText] = useState(
    globsToText(initial?.excludeGlobs, CODE_SOURCE_DEFAULT_EXCLUDE_GLOBS),
  );
  const [submitting, setSubmitting] = useState(false);

  const requiresGroup =
    defaultClassification === 'confidential' || defaultClassification === 'restricted';
  const groupError = requiresGroup && defaultAccessGroups.length === 0;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!repoRef.trim()) return;
    if (groupError) {
      onError('Confidential or Restricted classification requires at least one access group.');
      return;
    }
    setSubmitting(true);
    const includeGlobs = textToGlobs(includeText);
    const excludeGlobs = textToGlobs(excludeText);
    const classificationPayload = {
      ...(defaultClassification !== 'internal' ? { defaultClassification } : {}),
      ...(defaultAccessGroups.length > 0 ? { defaultAccessGroups } : {}),
    };
    try {
      if (mode === 'create') {
        await apiPost('/api/v1/admin/code-sources', {
          provider,
          repoRef: repoRef.trim(),
          branch: branch.trim() || 'main',
          ring,
          label: label.trim() || undefined,
          includeGlobs,
          excludeGlobs,
          ...classificationPayload,
        });
      } else if (initial) {
        await apiPatch(`/api/v1/admin/code-sources/${initial.id}`, {
          branch: branch.trim() || 'main',
          ring,
          label: label.trim() || undefined,
          includeGlobs,
          excludeGlobs,
          applyToExisting,
          ...classificationPayload,
        });
      }
      onSaved();
    } catch (err) {
      onError(errorMessage(err, 'Could not save code source.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
      <form
        onSubmit={submit}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-md bg-nebula-surface p-6 shadow-lg"
      >
        <div className="flex items-start justify-between">
          <h2 className="ov-h-section">
            {mode === 'create' ? 'Connect new repo' : 'Edit code source'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-tint"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cs-provider">
              Provider
            </label>
            <select
              id="cs-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value as CodeProvider)}
              disabled={mode === 'edit'}
              className="mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none disabled:bg-nebula-gray-5"
            >
              <option value="github">GitHub</option>
              <option value="bitbucket">Bitbucket Cloud (via Atlassian OAuth)</option>
              <option value="azure_devops">Azure DevOps</option>
              <option value="atlassian_rovo">Atlassian (Jira / Confluence)</option>
            </select>
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Use GitHub, Bitbucket Cloud or Azure DevOps for repository ingest. The Atlassian (Jira
              / Confluence) option is reserved for non-code retrieval flows and will refuse a
              code-source sync. For Azure DevOps the gateway uses the configured 1Nebula
              organisation; pass <code className="font-mono">project/repo</code> as the repo
              reference.
            </p>
            {mode === 'edit' && (
              <p className="ov-meta mt-1 text-nebula-fg-3">
                Provider is fixed once a source is created. Delete and re-add to switch.
              </p>
            )}
          </div>

          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cs-repo">
              Repo reference
            </label>
            <input
              id="cs-repo"
              required
              value={repoRef}
              onChange={(e) => setRepoRef(e.target.value)}
              disabled={mode === 'edit'}
              className="mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none disabled:bg-nebula-gray-5"
              placeholder={
                provider === 'azure_devops'
                  ? 'project/repo'
                  : provider === 'bitbucket'
                    ? 'workspace/repo'
                    : 'owner/repo'
              }
            />
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Gateway-defined slug. The gateway resolves provider, workspace and repo from this
              value — copy it from the aiagents.1nebula.com console.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cs-branch">
                Branch
              </label>
              <input
                id="cs-branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
                placeholder="main"
              />
            </div>
            <div>
              <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cs-label">
                Display label (optional)
              </label>
              <input
                id="cs-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
                placeholder="Knowledge agent"
              />
            </div>
          </div>

          <fieldset>
            <legend className="ov-meta-upper text-nebula-fg-2">Ring</legend>
            <div className="mt-2 space-y-2">
              <label className="flex items-start gap-2 text-sm text-nebula-fg-1">
                <input
                  type="radio"
                  name="cs-ring"
                  checked={ring === 'ring1'}
                  onChange={() => setRing('ring1')}
                  className="mt-0.5 accent-nebula-primary"
                />
                <span>
                  <span className="font-semibold">Product</span> (ring1) — broader access
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-nebula-fg-1">
                <input
                  type="radio"
                  name="cs-ring"
                  checked={ring === 'ring0'}
                  onChange={() => setRing('ring0')}
                  className="mt-0.5 accent-nebula-primary"
                />
                <span>
                  <span className="font-semibold">Internal</span> (ring0) — internal staff only
                </span>
              </label>
            </div>
          </fieldset>

          <fieldset>
            <legend className="ov-meta-upper text-nebula-fg-2">Default classification</legend>
            <p className="ov-meta mt-1 text-nebula-fg-3">
              Cascaded onto every chunk ingested from this repo. Operators can downgrade per-doc
              later.
            </p>
            <select
              value={defaultClassification}
              onChange={(e) =>
                setDefaultClassification(
                  e.target.value as 'public' | 'internal' | 'confidential' | 'restricted',
                )
              }
              className="mt-2 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
            >
              <option value="public">public</option>
              <option value="internal">internal</option>
              <option value="confidential">confidential</option>
              <option value="restricted">restricted</option>
            </select>
            {requiresGroup && (
              <label className="mt-2 flex items-center gap-2 text-sm text-nebula-fg-1">
                <input
                  type="checkbox"
                  checked={defaultAccessGroups.includes('confidential')}
                  onChange={(e) =>
                    setDefaultAccessGroups(
                      e.target.checked
                        ? Array.from(new Set([...defaultAccessGroups, 'confidential']))
                        : defaultAccessGroups.filter((g) => g !== 'confidential'),
                    )
                  }
                  className="accent-nebula-primary"
                />
                <span>Access group: confidential (KB-Confidential members)</span>
              </label>
            )}
            {groupError && (
              <p className="ov-meta mt-1 text-nebula-error">
                Confidential or Restricted requires at least one access group.
              </p>
            )}
            {mode === 'edit' && (
              <label className="mt-2 flex items-center gap-2 text-sm text-nebula-fg-1">
                <input
                  type="checkbox"
                  checked={applyToExisting}
                  onChange={(e) => setApplyToExisting(e.target.checked)}
                  className="accent-nebula-primary"
                />
                <span>Apply to existing chunks from this source</span>
              </label>
            )}
          </fieldset>

          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cs-include">
              Include globs
            </label>
            <textarea
              id="cs-include"
              rows={6}
              value={includeText}
              onChange={(e) => setIncludeText(e.target.value)}
              className="mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-[12px] text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
            />
            <p className="ov-meta mt-1 text-nebula-fg-3">
              One glob per line, relative to repo root. Pre-filled from the platform default set.
            </p>
          </div>

          <div>
            <label className="ov-meta-upper text-nebula-fg-2" htmlFor="cs-exclude">
              Exclude globs
            </label>
            <textarea
              id="cs-exclude"
              rows={6}
              value={excludeText}
              onChange={(e) => setExcludeText(e.target.value)}
              className="mt-1 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-[12px] text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-nebula-primary px-4 py-2 font-ui text-white hover:bg-nebula-primary/90 disabled:opacity-60"
          >
            {submitting ? 'Saving…' : mode === 'create' ? 'Create source' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- delete-by-typing-label modal ------------------------------------

function DeleteCodeSourceModal({
  source,
  onCancel,
  onConfirm,
}: {
  source: CodeSource;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const expected = source.label || source.repoRef;
  const [typed, setTyped] = useState('');
  const matches = typed === expected;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nebula-fg-1/40 p-4">
      <div className="w-full max-w-md rounded-md bg-nebula-surface p-6 shadow-lg">
        <h2 className="ov-h-section">Delete code source?</h2>
        <p className="ov-body mt-2 text-nebula-fg-3">
          This removes the gateway registration for <strong>{expected}</strong>. Already-ingested
          code chunks stay in the knowledgebase — remove them separately if you need them gone.
        </p>
        <p className="ov-body mt-3 text-nebula-fg-3">
          Type{' '}
          <code className="rounded bg-nebula-gray-5 px-1 py-0.5 font-mono text-[12px] text-nebula-fg-1">
            {expected}
          </code>{' '}
          to confirm:
        </p>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          className="mt-2 w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 font-mono text-sm text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm font-ui text-nebula-fg-1 hover:bg-nebula-tint"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!matches}
            onClick={onConfirm}
            className="rounded-md bg-nebula-error px-4 py-2 font-ui text-white hover:bg-nebula-error/90 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
